
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import { supabase } from '../../utils/supabase';

interface PersonalInfo {
  name: string;
  title: string;
  email: string;
  phone: string;
  location: string;
  summary: string;
  avatar: string;
}

const DEFAULT_PERSONAL_INFO: PersonalInfo = {
  name: 'Yating Qiu (邱雅婷)',
  title: '北京师范大学',
  email: '202631051086@mail.bnu.edu.cn',
  phone: '',
  location: '北京市',
  summary: '2026年福州大学优秀毕业生 特等学业奖学金',
  avatar: ''
};

const DEFAULT_HOME_IMAGES = [
  'https://qwmivbesrkczgkhiudja.supabase.co/storage/v1/object/public/site-images/legacy/home/1564866e-fd55-4d9d-8c55-e6695af1f0b0.jpg',
  'https://qwmivbesrkczgkhiudja.supabase.co/storage/v1/object/public/site-images/legacy/home/62b6eeb5-5942-4834-8f67-7f79dcbb8f08.jpg',
  'https://qwmivbesrkczgkhiudja.supabase.co/storage/v1/object/public/site-images/home/bd8cb5bf-829e-4156-9d0c-7fa66f3e8702.jpg',
  'https://qwmivbesrkczgkhiudja.supabase.co/storage/v1/object/public/site-images/home/7dcbdc73-ab3b-4596-95f1-a96a561ce0f2.jpg',
  'https://qwmivbesrkczgkhiudja.supabase.co/storage/v1/object/public/site-images/home/825c73c0-b716-4320-a01e-69dd2ae99cff.jpg',
  'https://qwmivbesrkczgkhiudja.supabase.co/storage/v1/object/public/site-images/home/c3f9869d-d607-4cae-b162-349167e1e80d.jpg'
];

function normalizeHomeImages(images: any[] | undefined) {
  if (!Array.isArray(images)) return [];

  return images
    .map((image) => {
      if (typeof image === 'string') return image;
      return image?.url || image?.image_url || '';
    })
    .filter((url): url is string => typeof url === 'string' && url.trim() !== '');
}

function normalizePreviewItems(items: any[] | undefined) {
  if (!Array.isArray(items)) return [];

  return items.map((item) => ({
    ...item,
    id: item.id || `${item.title || item.name || item.company || Date.now()}`,
    name: item.name || item.title,
    date: item.date || item.year,
    year: item.year || item.date,
    startDate: item.startDate || item.start_date,
    endDate: item.endDate || item.end_date,
  }));
}

function readCachedHomepageData() {
  try {
    const homepageData = localStorage.getItem('homepageData');
    if (homepageData) return JSON.parse(homepageData);
  } catch (error) {
    console.warn('读取首页缓存失败:', error);
  }

  return null;
}

function removeLegacyAdminDataCache() {
  try {
    localStorage.removeItem('adminData');
  } catch (error) {
    console.warn('清理旧首页缓存失败:', error);
  }
}

async function fetchHomepageData() {
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }

  const [personalInfo, homeImages, experience, projects, papers] = await Promise.allSettled([
    supabase.from('personal_info').select('*').limit(1).maybeSingle(),
    supabase.from('home_images').select('*').order('id', { ascending: true }),
    supabase.from('experience').select('*').order('start_date', { ascending: false }).limit(3),
    supabase.from('projects').select('*').order('start_date', { ascending: false }).limit(3),
    supabase.from('papers').select('*').limit(3),
  ]);

  const readResult = <T,>(result: PromiseSettledResult<{ data: T; error: any }>, fallback: T) => {
    if (result.status === 'fulfilled' && !result.value.error) return result.value.data || fallback;
    return fallback;
  };

  return {
    personalInfo: readResult(personalInfo, null),
    homeImages: normalizeHomeImages(readResult(homeImages, [])),
    experience: normalizePreviewItems(readResult(experience, [])),
    projects: normalizePreviewItems(readResult(projects, [])),
    papers: normalizePreviewItems(readResult(papers, [])),
  };
}

export default function Home() {
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>(DEFAULT_PERSONAL_INFO);
  const [homeImages, setHomeImages] = useState<string[]>(DEFAULT_HOME_IMAGES);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [experience, setExperience] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [papers, setPapers] = useState<any[]>([]);

  useEffect(() => {
    let isActive = true;
    removeLegacyAdminDataCache();

    const applyHomepageData = (data: any) => {
      if (!data || !isActive) return;

      if (data.personalInfo) {
        setPersonalInfo({ ...DEFAULT_PERSONAL_INFO, ...data.personalInfo });
      }

      const validHomeImages = normalizeHomeImages(data.homeImages);
      if (validHomeImages.length > 0) {
        setHomeImages(validHomeImages);
      }

      setExperience(normalizePreviewItems(data.experience));
      setProjects(normalizePreviewItems(data.projects));
      setPapers(normalizePreviewItems(data.papers));
    };

    const loadData = async () => {
      const cachedData = readCachedHomepageData();
      if (cachedData) {
        applyHomepageData(cachedData);
        setIsLoading(false);
      }

      try {
        const freshData = await fetchHomepageData();
        if (!isActive) return;

        applyHomepageData(freshData);
        setSaveStatus('');

        try {
          localStorage.setItem('homepageData', JSON.stringify(freshData));
        } catch (storageError) {
          console.warn('保存首页缓存失败:', storageError);
        }
      } catch (error) {
        console.warn('首页云端数据加载失败，使用真实默认内容:', error);
        if (isActive) {
          setSaveStatus('');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadData();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'homepageData') {
        applyHomepageData(readCachedHomepageData());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      isActive = false;
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #64748b, #cbd5e1)'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在加载数据...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-slate-200">
      <Navbar />
      
      {/* 状态提示 */}
      {saveStatus && (
        <div className="fixed top-20 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <i className="ri-check-line"></i>
            <span className="text-sm">{saveStatus}</span>
          </div>
        </div>
      )}
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{personalInfo.name}</h1>
            <p className="text-xl text-gray-600 mb-4">{personalInfo.title}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-gray-600">
              <div className="flex items-center">
                <i className="ri-mail-line mr-2"></i>
                <span>{personalInfo.email}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-map-pin-line mr-2"></i>
                <span>{personalInfo.location}</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-1 gap-8">
            {/* Recent Updates */}
            <div className="lg:col-span-1">
              <Card className="p-8" glass>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-notification-line mr-3 text-blue-600"></i>
                  近期动态
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">{personalInfo.summary}</p>
              </Card>
            </div>
            {/* Papers Preview */}
            {papers && papers.length > 0 && (
              <Card className="p-8" glass>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <i className="ri-file-text-line mr-3 text-blue-600"></i>
                    Publications
                  </h2>
                  <button onClick={() => window.REACT_APP_NAVIGATE('/papers')} className="text-blue-600 hover:text-blue-800 cursor-pointer">查看全部</button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {papers.slice(0, 3).map((paper: any) => (
                    <div key={paper.id} className="bg-white/70 rounded-lg p-5 border hover:shadow-md transition-shadow">
                      <div className="font-semibold text-gray-900 line-clamp-2">{paper.title}</div>
                      <div className="text-blue-600 text-sm">{paper.journal} • {paper.year}</div>
                      <div className="text-gray-500 text-xs mt-1">{paper.authors}</div>
                      <div className="text-gray-700 text-sm mt-2 line-clamp-3">{paper.abstract}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {/* Experience Preview */}
            {experience && experience.length > 0 && (
              <Card className="p-8" glass>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <i className="ri-briefcase-line mr-3 text-blue-600"></i>
                    Service
                  </h2>
                  <button onClick={() => window.REACT_APP_NAVIGATE('/experience')} className="text-blue-600 hover:text-blue-800 cursor-pointer">查看全部</button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {experience.slice(0, 3).map((exp: any) => (
                    <div key={exp.id} className="bg-white/70 rounded-lg p-5 border hover:shadow-md transition-shadow">
                      <div className="font-semibold text-gray-900">{exp.company}</div>
                      <div className="text-blue-600 text-sm mb-1">{exp.position}</div>
                      <div className="text-gray-500 text-sm mb-2">{exp.startDate} - {exp.endDate}</div>
                      <div className="text-gray-700 text-sm line-clamp-3">{exp.description}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {/* Projects/Patents Preview */}
            {projects && projects.length > 0 && (
              <Card className="p-8" glass>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <i className="ri-code-box-line mr-3 text-blue-600"></i>
                    Program
                  </h2>
                  <button onClick={() => window.REACT_APP_NAVIGATE('/patents')} className="text-blue-600 hover:text-blue-800 cursor-pointer">查看全部</button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.slice(0, 3).map((proj: any) => (
                    <div key={proj.id} className="bg-white/70 rounded-lg p-5 border hover:shadow-md transition-shadow">
                      <div className="font-semibold text-gray-900">{proj.name}</div>
                      <div className="text-blue-600 text-sm mb-1">{proj.startDate} - {proj.endDate}</div>
                      <div className="text-gray-700 text-sm line-clamp-3">{proj.description}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {/* More Gallery */}
            {homeImages && homeImages.length > 0 && (
              <Card className="p-8" glass>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-image-line mr-3 text-blue-600"></i>
                  More
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {homeImages.map((image, index) => (
                    <div key={index} className="relative group overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`学习项目 ${index + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-48 object-cover object-top transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
